<template>
  <b-modal
    id="restriktionenSperrzeitModal"
    centered
    scrollable
    no-close-on-backdrop
    @show="onShow"
    @hidden="onHidden"
  >
    <template #modal-header="{ close }">
      <div class="restriktionen-sperrzeit__header">
        <span class="restriktionen-sperrzeit__title">
          {{ isEditing ? 'Sperrzeit bearbeiten' : 'Sperrzeit hinzufügen' }}
        </span>
        <span class="restriktionen-sperrzeit__close" @click="close()">
          <X :size="20" />
        </span>
      </div>
    </template>

    <div class="restriktionen-sperrzeit__body">
      <div class="restriktionen-sperrzeit__field">
        <FeldLabel :icon="labelIcons.CalendarOff" text="Typ der Restriktion" :status="feldStatus.typ" />
        <div class="restriktionen-sperrzeit__tabs">
          <b-button
            v-for="option in restriktionstypOptions"
            :key="option.value"
            class="restriktionen-sperrzeit__tab dialog-btn-secondary"
            :class="{ 'restriktionen-sperrzeit__tab--active': form.restriktionstyp === option.value }"
            @click="form.restriktionstyp = option.value"
          >
            {{ option.text }}
          </b-button>
        </div>
      </div>

      <!-- Zuordnung Land, entfällt sobald Reisen gewählt sind (Backend erlaubt genau eins von beiden) -->
      <div class="restriktionen-sperrzeit__field">
        <FeldLabel
          :icon="labelIcons.MapPinPlus"
          text="Land"
          :status="feldStatus.land"
          status-tooltip="Land oder Reise muss ausgewählt sein"
        />
        <!-- inline, damit die lange Optionsliste den Dialog verlängert statt über die Felder zu klappen -->
        <AppSelect
          label="Land"
          placeholder="Land wählen"
          multiple
          sort="selected-first"
          :options="landSelectOptions"
          :value="form.laender"
          @input="onZuordnungInput('laender', $event)"
        />
      </div>

      <!-- Zuordnung Reise, eine Auswahl leert die Länderauswahl -->
      <div class="restriktionen-sperrzeit__field">
        <FeldLabel
          :icon="labelIcons.Globe"
          text="Reise"
          :status="feldStatus.reise"
          status-tooltip="Land oder Reise muss ausgewählt sein"
        />
        <AppSelect
          label="Reise"
          placeholder="Reise wählen"
          multiple
          sort="selected-first"
          :options="reiseOptions"
          :value="form.reisen"
          @input="onZuordnungInput('reisen', $event)"
        />
      </div>

      <!-- Grund als Einfachauswahl -->
      <div class="restriktionen-sperrzeit__field">
        <FeldLabel :icon="labelIcons.Tag" text="Grund" :status="feldStatus.grund" />
        <AppSelect
          v-model="form.grund"
          label="Grund"
          sort="alpha"
          hide-search
          placeholder="Grund wählen"
          :options="grundOptions"
        />
      </div>

      <!-- Kommentar -->
      <div class="restriktionen-sperrzeit__field">
        <FeldLabel :icon="labelIcons.MessagesSquare" text="Kommentar" optional />
        <b-form-textarea
          v-model="form.kommentar"
          class="restriktionen-sperrzeit__textarea"
          placeholder="Kommentar eingeben ..."
          rows="3"
          no-resize
        />
      </div>

      <!-- Wiederholung -->
      <div class="restriktionen-sperrzeit__field">
        <FeldLabel :icon="labelIcons.Repeat" text="Wiederholung" :status="feldStatus.wiederholung" />
        <RestriktionenSegmentedControl :options="wiederholungTabs" v-model="form.tab" />
      </div>

      <!-- Einmalig: Von und optionales Bis -->
      <div v-if="form.tab === 'einmalig'" class="restriktionen-sperrzeit__row">
        <SperrzeitDatumFeld
          v-model="form.von"
          label="Von"
          :status="feldStatus.von"
          :min="vonMin"
          :max="vonMax"
        />
        <SperrzeitDatumFeld v-model="form.bis" label="Bis" optional :min="endeMin" />
      </div>

      <!-- Wöchentlich und Monatlich teilen sich Von und Turnus, nur die Turnus-Beschriftung unterscheidet sich -->
      <div
        v-if="form.tab === 'woechentlich' || form.tab === 'monatlich'"
        class="restriktionen-sperrzeit__row"
      >
        <SperrzeitDatumFeld
          v-model="form.von"
          label="Von"
          :status="feldStatus.von"
          :min="vonMin"
          :max="vonMax"
        />
        <div class="restriktionen-sperrzeit__field">
          <FeldLabel :icon="labelIcons.Repeat" :text="turnusLabel" />
          <AppSelect
            v-model="form.turnus"
            label="Turnus"
            hide-search
            placeholder="Turnus wählen"
            :options="turnusOptions"
          />
        </div>
      </div>

      <!-- Wöchentlich: Auswahl der Wochentage -->
      <div v-if="form.tab === 'woechentlich'" class="restriktionen-sperrzeit__field">
        <FeldLabel text="Wochentage" :status="feldStatus.wochentage" />
        <div class="restriktionen-sperrzeit__chips">
          <button
            v-for="tag in wochentageOptions"
            :key="tag.value"
            type="button"
            class="restriktionen-sperrzeit__chip"
            :class="{ 'restriktionen-sperrzeit__chip--active': form.wochentage.includes(tag.value) }"
            @click="toggleWochentag(tag.value)"
          >
            {{ tag.kurz }}
          </button>
        </div>
      </div>

      <!-- Monatlich: Modus (Monatstag oder n-ter Wochentag) -->
      <template v-if="form.tab === 'monatlich'">
        <div class="restriktionen-sperrzeit__field">
          <FeldLabel :icon="labelIcons.Calendar" text="Wann" />
          <RestriktionenSegmentedControl :options="monatModusOptions" v-model="form.monatModus" />
        </div>

        <div v-if="form.monatModus === 'wochentag'" class="restriktionen-sperrzeit__row">
          <div class="restriktionen-sperrzeit__field">
            <FeldLabel :icon="labelIcons.Repeat" text="Jeder" />
            <AppSelect
              v-model="form.ntesVorkommen"
              label="Vorkommen"
              hide-search
              placeholder="Vorkommen wählen"
              :options="ntesVorkommenOptions"
            />
          </div>
          <div class="restriktionen-sperrzeit__field">
            <FeldLabel :icon="labelIcons.Calendar" text="Wochentag" />
            <AppSelect
              v-model="form.monatWochentag"
              label="Wochentag"
              hide-search
              placeholder="Wochentag wählen"
              :options="wochentagSelectOptions"
            />
          </div>
        </div>
      </template>

      <!-- Serienende für Wöchentlich und Monatlich -->
      <template v-if="form.tab === 'woechentlich' || form.tab === 'monatlich'">
        <div class="restriktionen-sperrzeit__field">
          <FeldLabel :icon="labelIcons.Calendar" text="Endet" :status="feldStatus.endet" />
          <RestriktionenSegmentedControl :options="endetOptions" v-model="form.endet" />
        </div>

        <SperrzeitDatumFeld v-if="form.endet === 'datum'" v-model="form.serieBis" :min="endeMin" />
      </template>

      <!-- Jährlich: entspricht Monatlich mit Turnus 12 am Kalendertag des Startdatums -->
      <template v-if="form.tab === 'jaehrlich'">
        <div class="restriktionen-sperrzeit__banner restriktionen-sperrzeit__banner--info">
          <span class="restriktionen-sperrzeit__banner-icon">
            <Info :size="18" />
          </span>
          <span>Wiederholt sich jedes Jahr am gleichen Datum.</span>
        </div>
        <div class="restriktionen-sperrzeit__row">
          <SperrzeitDatumFeld
            v-model="form.von"
            label="Beginn"
            :status="feldStatus.von"
            :min="vonMin"
            :max="vonMax"
          />
          <SperrzeitDatumFeld v-model="form.serieBis" label="Ende" optional :min="endeMin" />
        </div>
      </template>

      <!-- Zusammenfassung der gewählten Wiederholung, weicht dem Duplikat-Hinweis -->
      <div
        v-if="summaryText && !duplicateEntries.length"
        class="restriktionen-sperrzeit__banner restriktionen-sperrzeit__banner--info"
      >
        <span class="restriktionen-sperrzeit__banner-icon">
          <Info :size="16" />
        </span>
        <span>{{ summaryText }}</span>
      </div>

      <!-- Hinweis auf bereits bestehende Sperrzeiten, blockiert das Anlegen nicht -->
      <div
        v-if="duplikate.length"
        class="restriktionen-sperrzeit__banner restriktionen-sperrzeit__banner--warning restriktionen-sperrzeit__banner--liste"
      >
        <div class="restriktionen-sperrzeit__duplikat-kopf">
          <span class="restriktionen-sperrzeit__banner-icon restriktionen-sperrzeit__banner-icon--warning">
            <TriangleAlert :size="16" />
          </span>
          <strong>{{ duplicateTitel }}</strong>
        </div>
        <div v-for="(gruppe, index) in duplikate" :key="index" class="restriktionen-sperrzeit__duplikat">
          <span
            class="restriktionen-sperrzeit__duplikat-badge"
            :style="{
              backgroundColor: typeConfig(gruppe.type).bgColor,
              color: typeConfig(gruppe.type).textColor,
            }"
          >
            <component :is="typeConfig(gruppe.type).icon" :size="12" />
            {{ typeConfig(gruppe.type).modalLabel }}
          </span>
          <span class="restriktionen-sperrzeit__duplikat-zeitraum">
            {{ zeitraumKompakt(gruppe) }}
          </span>
        </div>
      </div>

      <!-- Hinweis beim Bearbeiten, sobald eines der Serien-Attribute geändert wurde -->
      <div
        v-if="zeigeSerienWarnung"
        class="restriktionen-sperrzeit__banner restriktionen-sperrzeit__banner--warning"
      >
        <span class="restriktionen-sperrzeit__banner-icon restriktionen-sperrzeit__banner-icon--warning">
          <TriangleAlert :size="16" />
        </span>
        <span>Beim Speichern der Serie werden zuvor entfernte Einzeltage wiederhergestellt.</span>
      </div>
    </div>

    <template #modal-footer>
      <b-button
        class="dialog-btn-secondary"
        :disabled="isSaving"
        @click="$bvModal.hide('restriktionenSperrzeitModal')"
      >
        Abbrechen
      </b-button>
      <b-button class="dialog-btn" variant="primary" :disabled="!isFormValid || isSaving" @click="onSubmit">
        <b-spinner v-if="isSaving" small class="mr-2" />
        {{ isEditing ? 'Speichern' : 'Sperrzeit hinzufügen' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import moment from 'moment-timezone';
import {
  X,
  SlidersHorizontal,
  Globe,
  CalendarOff,
  MapPinPlus,
  Plane,
  Tag,
  MessagesSquare,
  Repeat,
  Calendar,
  Info,
  TriangleAlert,
} from 'lucide-vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import RestriktionenSegmentedControl from './restriktionen-segmented-control.vue';
import FeldLabel from '@/components/common/feld-label.vue';
import SperrzeitDatumFeld from './sperrzeit-datum-feld.vue';
import {
  RESTRIKTIONSTYP_OPTIONS,
  RESTRIKTIONSGRUND_OPTIONS,
  SERIE_BIS_UNBEFRISTET,
  TAB_ZU_WIEDERHOLUNGSTYP,
  getWiederholungTab,
  mapRestriktionstyp,
  sortRestriktionenByPriority,
} from './restriktionen.constants.js';
import {
  DAY_KEY_FORMAT,
  WOCHENTAGE_OPTIONS,
  NTES_VORKOMMEN_OPTIONS,
  formatSperrzeitZusammenfassung,
  formatDatumLang,
  formatZeitraumKompakt,
  istUnbefristet,
} from './restriktionen-calendar.helper.js';
import restriktionenTypMixin from './restriktionen-typ.mixin.js';
import {
  getRestriktionskonfiguration,
  saveRestriktionskonfiguration,
} from '@/core/restriktionen/services/restriktionskonfiguration.service.js';
import { germanCountryName } from '@/core/common/iso3166Countries.js';

const MIN_STARTDATUM = '2026-01-01';
const MAX_STARTDATUM_SERIE = '2049-12-31';

export default {
  name: 'RestriktionenSperrzeitModalComponent',
  // Das Mixin liefert typeConfig sowie die Icon-Komponenten der Restriktionstypen
  mixins: [restriktionenTypMixin],
  components: {
    AppSelect,
    RestriktionenSegmentedControl,
    FeldLabel,
    SperrzeitDatumFeld,
    X,
    Info,
    TriangleAlert,
  },
  props: {
    prefillDate: { type: String, default: null },
    editEntry: { type: Object, default: null },
    landOptions: { type: Array, default: () => [] },
    reiseOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: this.leeresFormular(),
      isSaving: false,
      duplicateEntries: [],
      // Serienregel beim Öffnen des Dialogs als JSON, Vergleichsbasis für den Warnhinweis
      serienRegelOriginal: null,
      duplicateAbortController: null,
      duplicateDebounce: null,
      skipTabReset: false,
      labelIcons: Object.freeze({
        SlidersHorizontal,
        Globe,
        CalendarOff,
        MapPinPlus,
        Plane,
        Tag,
        MessagesSquare,
        Repeat,
        Calendar,
      }),
      restriktionstypOptions: RESTRIKTIONSTYP_OPTIONS,
      grundOptions: RESTRIKTIONSGRUND_OPTIONS,
      wochentageOptions: WOCHENTAGE_OPTIONS,
      ntesVorkommenOptions: NTES_VORKOMMEN_OPTIONS.map(option => ({
        value: option.value,
        text: option.text,
      })),
      wiederholungTabs: [
        { value: 'einmalig', text: 'Einmalig' },
        { value: 'woechentlich', text: 'Wöchentlich' },
        { value: 'monatlich', text: 'Monatlich' },
        { value: 'jaehrlich', text: 'Jährlich' },
      ],
      endetOptions: [
        { value: 'offen', text: 'Offen' },
        { value: 'datum', text: 'Bis Datum' },
      ],
    };
  },
  computed: {
    isEditing() {
      return !!this.editEntry;
    },
    wochentagSelectOptions() {
      return WOCHENTAGE_OPTIONS.map(option => ({ value: option.value, text: option.lang }));
    },
    wiederholungstyp() {
      return TAB_ZU_WIEDERHOLUNGSTYP[this.form.tab];
    },
    vonMoment() {
      return this.form.von ? moment(this.form.von) : null;
    },
    vonMin() {
      return MIN_STARTDATUM;
    },
    vonMax() {
      return this.wiederholungstyp !== 'Single' ? MAX_STARTDATUM_SERIE : null;
    },
    endeMin() {
      return this.form.von || MIN_STARTDATUM;
    },
    monatstagLabel() {
      return this.vonMoment ? this.vonMoment.date() : '…';
    },
    turnusLabel() {
      return this.form.tab === 'woechentlich' ? 'Alle ... Wochen' : 'Alle ... Monate';
    },
    monatModusOptions() {
      return [
        { value: 'tag', text: `Am ${this.monatstagLabel}. des Monats` },
        { value: 'wochentag', text: 'Am Wochentag' },
      ];
    },
    turnusOptions() {
      const laenge = this.form.tab === 'woechentlich' ? 8 : 12;
      return Array.from({ length: laenge }, (_, index) => ({
        value: index + 1,
        text: String(index + 1),
      }));
    },
    sortierteWochentage() {
      return WOCHENTAGE_OPTIONS.filter(option => this.form.wochentage.includes(option.value)).map(
        option => option.value
      );
    },
    landSelectOptions() {
      const bekannteCodes = new Set(this.landOptions.map(option => option.value));
      const fehlende = this.form.laender
        .filter(code => !bekannteCodes.has(code))
        .map(code => ({ value: code, text: germanCountryName(code) }));
      return fehlende.length ? [...this.landOptions, ...fehlende] : this.landOptions;
    },
    zuordnungValid() {
      return this.form.laender.length > 0 || this.form.reisen.length > 0;
    },
    vonValid() {
      if (!this.vonMoment) {
        return false;
      }
      if (this.vonMoment.isBefore(MIN_STARTDATUM, 'day')) {
        return false;
      }
      if (this.wiederholungstyp !== 'Single' && this.vonMoment.isAfter(MAX_STARTDATUM_SERIE, 'day')) {
        return false;
      }
      return true;
    },
    dauer() {
      if (!this.form.bis || !this.vonMoment) {
        return 1;
      }
      return moment(this.form.bis).diff(this.vonMoment, 'days') + 1;
    },
    serieBisValid() {
      if (this.form.endet !== 'datum') {
        return true;
      }
      return !!this.form.serieBis && !moment(this.form.serieBis).isBefore(this.vonMoment, 'day');
    },
    // Gültigkeit der Wiederholungs-Felder je nach aktivem Tab
    wiederholungValid() {
      switch (this.form.tab) {
        case 'einmalig':
          // 365 Tage sind das Backend-Maximum für die Dauer einer einmaligen Sperrzeit
          return this.dauer >= 1 && this.dauer <= 365;
        case 'woechentlich':
          return this.form.turnus > 0 && this.form.wochentage.length >= 1 && this.serieBisValid;
        case 'monatlich':
          return (
            this.form.turnus > 0 &&
            this.serieBisValid &&
            (this.form.monatModus === 'tag' || (this.form.ntesVorkommen >= 1 && !!this.form.monatWochentag))
          );
        case 'jaehrlich':
          return !this.form.serieBis || !moment(this.form.serieBis).isBefore(this.vonMoment, 'day');
        default:
          return false;
      }
    },
    isFormValid() {
      return (
        !!this.form.restriktionstyp &&
        this.zuordnungValid &&
        this.form.grund !== null &&
        this.vonValid &&
        this.wiederholungValid
      );
    },
    summaryText() {
      const serieBisAnzeige =
        this.form.tab === 'jaehrlich'
          ? this.form.serieBis
          : this.form.endet === 'datum'
          ? this.form.serieBis
          : null;
      return formatSperrzeitZusammenfassung({
        tab: this.form.tab,
        von: this.form.von,
        bis: this.form.bis,
        turnus: this.form.turnus,
        wochentage: this.sortierteWochentage,
        monatModus: this.form.monatModus,
        ntesVorkommen: this.form.ntesVorkommen,
        monatWochentag: this.form.monatWochentag,
        serieBis: serieBisAnzeige,
      });
    },
    feldStatus() {
      return {
        typ: 'valid',
        land: !this.zuordnungValid ? 'invalid' : this.form.laender.length ? 'valid' : null,
        reise: !this.zuordnungValid ? 'invalid' : this.form.reisen.length ? 'valid' : null,
        grund: this.form.grund !== null ? 'valid' : 'invalid',
        wiederholung: 'valid',
        von: this.vonValid ? 'valid' : 'invalid',
        wochentage: this.form.wochentage.length >= 1 ? 'valid' : 'invalid',
        endet: this.serieBisValid ? 'valid' : 'invalid',
      };
    },
    serienRegel() {
      const typ = this.wiederholungstyp;
      const isSingle = typ === 'Single';
      const isJaehrlich = this.form.tab === 'jaehrlich';
      const wochentagModus = this.form.tab === 'monatlich' && this.form.monatModus === 'wochentag';

      let serieBis = null;
      if (!isSingle) {
        const ende = isJaehrlich || this.form.endet === 'datum' ? this.form.serieBis : null;
        serieBis = ende ? moment(ende).format(DAY_KEY_FORMAT) : SERIE_BIS_UNBEFRISTET;
      }

      let wochentage = null;
      if (this.form.tab === 'woechentlich') {
        wochentage = this.sortierteWochentage;
      } else if (wochentagModus) {
        wochentage = [this.form.monatWochentag];
      }

      return {
        wiederholungstyp: typ,
        startdatum: this.form.von ? moment(this.form.von).format(DAY_KEY_FORMAT) : null,
        dauer: isSingle ? this.dauer : typ === 'Weekly' ? null : 1,
        serieBis,
        turnus: isSingle ? null : isJaehrlich ? 12 : Number(this.form.turnus),
        wochentage,
        ntesVorkommenImMonat: wochentagModus ? this.form.ntesVorkommen : null,
      };
    },
    // Abweichung der Serienregel vom Stand beim Öffnen des Dialogs
    serienRegelGeaendert() {
      return !!this.serienRegelOriginal && JSON.stringify(this.serienRegel) !== this.serienRegelOriginal;
    },
    // Beim Speichern erzeugt das Backend die Termine neu und stellt entfernte Einzeltage wieder her
    zeigeSerienWarnung() {
      return this.isEditing && this.editEntry.wiederholungstyp !== 'Single' && this.serienRegelGeaendert;
    },
    duplikate() {
      const mitTyp = this.duplicateEntries.map(gruppe => ({
        ...gruppe,
        type: mapRestriktionstyp(gruppe.restriktionstyp, gruppe.wiederholungstyp),
      }));
      return sortRestriktionenByPriority(mitTyp);
    },
    duplicateTitel() {
      const anzahl = this.duplikate.length;
      const bestand = anzahl === 1 ? 'besteht bereits 1 Sperrzeit' : `bestehen bereits ${anzahl} Sperrzeiten`;
      const bis = this.form.tab === 'einmalig' ? this.form.bis : null;
      if (bis && !moment(bis).isSame(this.form.von, 'day')) {
        return `Im Zeitraum ${formatDatumLang(this.form.von)} bis ${formatDatumLang(bis)} ${bestand}`;
      }
      return `Am ${formatDatumLang(this.form.von)} ${bestand}`;
    },
    duplicateCheckKey() {
      if (!this.form.von || !this.zuordnungValid) {
        return null;
      }
      const von = moment(this.form.von).format(DAY_KEY_FORMAT);
      const bis =
        this.form.tab === 'einmalig' && this.form.bis ? moment(this.form.bis).format(DAY_KEY_FORMAT) : von;
      return JSON.stringify({
        von,
        bis,
        laender: this.form.laender,
        reisen: this.form.reisen,
      });
    },
  },
  watch: {
    'form.tab'() {
      if (this.skipTabReset) {
        this.skipTabReset = false;
        return;
      }
      this.form.turnus = 1;
      this.form.wochentage = [];
      this.form.monatModus = 'tag';
      this.form.ntesVorkommen = 1;
      this.form.monatWochentag = 'Monday';
      this.form.endet = 'offen';
      this.form.serieBis = null;
      this.form.bis = null;
    },
    duplicateCheckKey(key) {
      clearTimeout(this.duplicateDebounce);
      if (this.duplicateAbortController) {
        this.duplicateAbortController.abort();
      }
      if (!key) {
        this.duplicateEntries = [];
        return;
      }
      this.duplicateDebounce = setTimeout(() => this.checkDuplicates(JSON.parse(key)), 400);
    },
  },
  beforeDestroy() {
    this.cleanupDuplicateCheck();
  },
  methods: {
    leeresFormular() {
      return {
        restriktionstyp: 'GesperrteAbreise',
        laender: [],
        reisen: [],
        grund: null,
        kommentar: '',
        tab: 'einmalig',
        von: null,
        bis: null,
        turnus: 1,
        wochentage: [],
        monatModus: 'tag',
        ntesVorkommen: 1,
        monatWochentag: 'Monday',
        endet: 'offen',
        serieBis: null,
      };
    },
    onShow() {
      this.form = this.leeresFormular();
      this.duplicateEntries = [];
      this.serienRegelOriginal = null;
      if (this.editEntry) {
        this.fillFromEntry(this.editEntry);
        return;
      }
      if (this.prefillDate) {
        this.form.von = this.prefillDate;
      }
    },
    fillFromEntry(entry) {
      const wochentage = entry.wochentage || [];
      const serieOffen = istUnbefristet(entry.serieBis);
      const serieBis = serieOffen ? null : moment(entry.serieBis).format(DAY_KEY_FORMAT);
      const tab = getWiederholungTab(entry);

      const start = moment(entry.startdatum);

      this.skipTabReset = tab !== this.form.tab;
      this.form = {
        restriktionstyp: entry.restriktionstyp,
        laender: entry.laenderCodes || [],
        reisen: entry.reisekuerzel || [],
        grund: entry.restriktionsgrund ?? null,
        kommentar: entry.kommentar || '',
        tab,
        von: start.format(DAY_KEY_FORMAT),
        bis:
          tab === 'einmalig' && entry.dauer > 1
            ? start
                .clone()
                .add(entry.dauer - 1, 'days')
                .format(DAY_KEY_FORMAT)
            : null,
        turnus: tab === 'jaehrlich' ? 1 : entry.turnus || 1,
        wochentage: tab === 'woechentlich' ? [...wochentage] : [],
        monatModus: tab === 'monatlich' && wochentage.length ? 'wochentag' : 'tag',
        ntesVorkommen: entry.ntesVorkommenImMonat || 1,
        monatWochentag: tab === 'monatlich' && wochentage.length ? wochentage[0] : 'Monday',
        endet: serieOffen ? 'offen' : 'datum',
        serieBis,
      };
      this.$nextTick(() => {
        this.skipTabReset = false;
        this.serienRegelOriginal = JSON.stringify(this.serienRegel);
      });
    },
    onHidden() {
      this.cleanupDuplicateCheck();
    },
    cleanupDuplicateCheck() {
      clearTimeout(this.duplicateDebounce);
      if (this.duplicateAbortController) {
        this.duplicateAbortController.abort();
        this.duplicateAbortController = null;
      }
    },
    // Zuordnung zu Land oder Reise gesetzt und dabei das jeweils andere Feld geleert,
    onZuordnungInput(feld, auswahl) {
      const gegenstueck = feld === 'laender' ? 'reisen' : 'laender';
      this.form[feld] = auswahl;
      if (auswahl.length) {
        this.form[gegenstueck] = [];
      }
    },
    toggleWochentag(wert) {
      if (this.form.wochentage.includes(wert)) {
        this.form.wochentage = this.form.wochentage.filter(tag => tag !== wert);
      } else {
        this.form.wochentage = [...this.form.wochentage, wert];
      }
    },
    async checkDuplicates({ von, bis, laender, reisen }) {
      const controller = new AbortController();
      this.duplicateAbortController = controller;
      const result = await getRestriktionskonfiguration({
        von,
        bis,
        laender,
        reisen,
        signal: controller.signal,
      });
      if (controller.signal.aborted) {
        return;
      }
      const gruppen = [...((result && result.laender) || []), ...((result && result.reisen) || [])];
      const bekannt = new Set();
      this.duplicateEntries = gruppen.filter(gruppe => {
        if (bekannt.has(gruppe.id) || (this.editEntry && gruppe.id === this.editEntry.id)) {
          return false;
        }
        bekannt.add(gruppe.id);
        return true;
      });
    },
    zeitraumKompakt(gruppe) {
      return formatZeitraumKompakt(gruppe);
    },
    buildPayload() {
      const startdatum = this.serienRegel.startdatum;
      const payload = {
        ...this.serienRegel,
        restriktionstyp: this.form.restriktionstyp,
        restriktionsgrund: this.form.grund,
        kommentar: this.form.kommentar.trim() || null,
        reisekuerzel: this.form.reisen.length ? this.form.reisen : null,
        laender: this.form.reisen.length ? null : this.form.laender,
        responseFilter: { von: startdatum, bis: startdatum },
      };
      if (this.isEditing) {
        payload.id = this.editEntry.id;
      }
      return payload;
    },
    async onSubmit() {
      if (!this.isFormValid || this.isSaving) {
        return;
      }
      this.isSaving = true;
      const wirdBearbeitet = this.isEditing;
      try {
        await saveRestriktionskonfiguration(this.buildPayload());
        this.$bvToast.toast(wirdBearbeitet ? 'Sperrzeit wurde gespeichert.' : 'Sperrzeit wurde angelegt.', {
          variant: 'success',
          noCloseButton: true,
        });
        this.$bvModal.hide('restriktionenSperrzeitModal');
        this.$emit('created');
      } catch (error) {
        // Fehler wurde bereits im Service angezeigt, der Dialog bleibt für Korrekturen offen
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.restriktionen-sperrzeit__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.restriktionen-sperrzeit__title {
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: var(--text);
}

.restriktionen-sperrzeit__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-muted);
}

.restriktionen-sperrzeit__close:hover {
  color: var(--text-body);
}

.restriktionen-sperrzeit__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.restriktionen-sperrzeit__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.restriktionen-sperrzeit__row {
  display: flex;
  gap: 12px;
}

.restriktionen-sperrzeit__tabs {
  display: flex;
  gap: 8px;
}

.restriktionen-sperrzeit__tab {
  flex: 1;
  white-space: nowrap;
}

/* Selektorstruktur analog zu dialog-button-styles.scss, damit der blaue Rahmen auch die
   hoch spezifischen Hover-/Focus-Regeln des weißen Ghost-Stils überschreibt */
.btn.restriktionen-sperrzeit__tab--active,
.btn.restriktionen-sperrzeit__tab--active:hover:not(.btn-text):not(:disabled):not(.disabled),
.btn.restriktionen-sperrzeit__tab--active:focus:not(.btn-text),
.btn.restriktionen-sperrzeit__tab--active:not(:disabled):not(.disabled):active:not(.btn-text) {
  background-color: var(--surface);
  color: var(--text-body);
  font-size: 13px;
  line-height: 15px;
  font-weight: 500;
  border: 0.5px solid var(--primary);
}

.restriktionen-sperrzeit__textarea {
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 13px;
}

.restriktionen-sperrzeit__chips {
  display: flex;
  gap: 8px;
}

.restriktionen-sperrzeit__chip {
  flex: 1;
  height: var(--control-h-sm);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-body);
  cursor: pointer;
}

.restriktionen-sperrzeit__chip:hover {
  background: var(--surface-hover);
}

.restriktionen-sperrzeit__chip--active {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

.restriktionen-sperrzeit__banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: var(--radius-sm);
  background-color: #eff6ff;
  border: 1px solid #dbeafe;
  font-size: 13px;
  line-height: 20px;
  font-weight: 400;
  color: var(--text-body);
}

.restriktionen-sperrzeit__banner-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
}

.restriktionen-sperrzeit__banner--warning {
  background-color: #ffa80026;
  border-color: var(--warning);
  color: #d97706;
}

.restriktionen-sperrzeit__banner-icon--warning {
  color: #d97706;
}

/* Banner mit mehreren Zeilen */
.restriktionen-sperrzeit__banner--liste {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.restriktionen-sperrzeit__duplikat-kopf {
  display: flex;
  align-items: center;
  gap: 8px;
}

.restriktionen-sperrzeit__duplikat {
  display: flex;
  align-items: center;
  gap: 12px;
}

.restriktionen-sperrzeit__duplikat-badge {
  display: inline-flex;
  gap: 8px;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  font-size: 11px;
  line-height: 13px;
  font-weight: 500;
}

.restriktionen-sperrzeit__duplikat-zeitraum {
  font-size: 13px;
  color: var(--text-body);
}

.restriktionen-sperrzeit__field >>> .app-select__menu {
  max-width: 100%;
}
</style>
